import React, { useRef, useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CaretUpIcon,
  CaretDownIcon,
  DownloadIcon,
  CaretUpDownIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { TableSearchInput } from '@/components/common/TableSearchInput';
import { downloadCSV } from '@/utils/csvDownload';
import {
  FROZEN_HEADER_EDGE_CLASS,
  pinnedCellProps,
  pinnedHeaderCellProps,
  pinnedHeaderRowClass,
  pinnedRowClass,
  usePinnedColumns,
  type PinnedColumns,
} from './gentlyTablePinning';
import { TablePagination } from './TablePagination';
import { useTableSearch } from './hooks/useTableSearch';
import { useTableSort } from './hooks/useTableSort';

export type SortDirection = 'asc' | 'desc';

const EMPTY_SEARCH_FIELDS: never[] = [];

function SortIcon({
  direction,
  sortable,
}: {
  direction: 'asc' | 'desc' | null;
  sortable?: boolean;
}) {
  if (direction === 'asc') return <CaretUpIcon className="ml-1 h-4 w-4" />;
  if (direction === 'desc') return <CaretDownIcon className="ml-1 h-4 w-4" />;
  if (sortable)
    return (
      <CaretUpDownIcon className="ml-1 h-3 w-3 text-muted-foreground/40" />
    );
  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ExpandedRowContent<T extends Record<string, any>>({
  renderFn,
  row,
}: {
  renderFn: (row: T) => React.ReactNode;
  row: T;
}) {
  return <div className="overflow-x-auto">{renderFn(row)}</div>;
}

/**
 * What a body cell shows: the row number for the synthetic `index` column, then
 * the column's own renderer, the table's default renderer, or the raw value.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CellContent<T extends Record<string, any>>({
  column,
  row,
  index,
  defaultCellRender,
}: {
  column: TableColumn<T>;
  row: T;
  index: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultCellRender?: (value: any, row: T) => React.ReactNode;
}) {
  if (String(column.id) === 'index')
    return <span className="font-medium text-sm">{index + 1}</span>;
  if (column.render) return column.render(row[column.id], row);
  if (defaultCellRender) return defaultCellRender(row[column.id], row);
  return <span className="text-sm">{row[column.id] || '-'}</span>;
}

/**
 * Whether the reader can drag this column to a new position.
 *
 * Internal columns (id prefixed '__', e.g. the selection checkbox) and frozen
 * ones stay put. The frozen block is placed by column position — pinned columns
 * have to lead the array and be contiguous, see `buildPinnedColumns` — so a
 * frozen column dragged out of that leading run would stop being frozen, and
 * dnd-kit's transforms don't compose with sticky offsets anyway.
 */
export function isDraggableColumn<T>(column: TableColumn<T>): boolean {
  return !String(column.id).startsWith('__') && !column.pinned;
}

/**
 * A header cell that stays where it is: every column when reordering is off, and
 * the ones the reader can't move when it is on (see `isDraggableColumn`).
 */
function StaticTableHead<T>({
  column,
  pinned,
  sizeToContent,
  sortDirection,
  onSortClick,
}: {
  column: TableColumn<T>;
  pinned: PinnedColumns;
  sizeToContent?: boolean;
  sortDirection: 'asc' | 'desc' | null;
  onSortClick: (columnId: keyof T) => void;
}) {
  const pinnedProps = pinnedHeaderCellProps(column, pinned);
  return (
    <TableHead
      className={cn(
        column.align === 'center' && 'text-center',
        column.align === 'right' && 'text-right',
        column.sortable && 'cursor-pointer select-none',
        sizeToContent && 'whitespace-nowrap',
        pinnedProps.className,
        column.className,
      )}
      style={{ minWidth: column.minWidth, ...pinnedProps.style }}
      onClick={() => {
        if (column.sortable) onSortClick(column.id);
      }}
    >
      <HeaderCellContent column={column} sortDirection={sortDirection} />
    </TableHead>
  );
}

/**
 * The header row for the non-reordering path. (With `enableColumnReorder` the
 * header is built by `ReorderableHeaderRow` instead.) `ref` goes on the `<tr>`,
 * which is what frozen columns measure.
 */
function PlainHeaderRow<T>({
  ref,
  columns,
  pinned,
  sizeToContent,
  hasExpandColumn,
  getSortIcon,
  onSortClick,
}: {
  ref: React.Ref<HTMLTableRowElement>;
  columns: TableColumn<T>[];
  pinned: PinnedColumns;
  sizeToContent: boolean;
  hasExpandColumn: boolean;
  getSortIcon: (columnId: keyof T) => 'asc' | 'desc' | null;
  onSortClick: (columnId: keyof T) => void;
}) {
  return (
    <TableRow ref={ref} className={pinnedHeaderRowClass(pinned)}>
      {columns.map((column) => (
        <StaticTableHead
          key={String(column.id)}
          column={column}
          pinned={pinned}
          sizeToContent={sizeToContent}
          sortDirection={getSortIcon(column.id)}
          onSortClick={onSortClick}
        />
      ))}
      {hasExpandColumn && <TableHead className="w-10" />}
    </TableRow>
  );
}

/**
 * The header row with draggable headers, for the `enableColumnReorder` path.
 *
 * `DndContext`/`SortableContext` render no DOM of their own, so the table markup
 * stays valid. Columns the reader can't move render as static heads and keep
 * their place — including the frozen ones, which stay frozen while their
 * neighbours are dragged around them.
 */
function ReorderableHeaderRow<T>({
  ref,
  columns,
  pinned,
  sizeToContent,
  hasExpandColumn,
  getSortIcon,
  onSortClick,
  sensors,
  onDragEnd,
}: {
  ref: React.Ref<HTMLTableRowElement>;
  columns: TableColumn<T>[];
  pinned: PinnedColumns;
  sizeToContent: boolean;
  hasExpandColumn: boolean;
  getSortIcon: (columnId: keyof T) => 'asc' | 'desc' | null;
  onSortClick: (columnId: keyof T) => void;
  sensors: ReturnType<typeof useSensors>;
  onDragEnd: (event: DragEndEvent) => void;
}) {
  const draggableIds = columns
    .filter(isDraggableColumn)
    .map((column) => String(column.id));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={draggableIds}
        strategy={horizontalListSortingStrategy}
      >
        <TableRow ref={ref} className={pinnedHeaderRowClass(pinned)}>
          {columns.map((column) =>
            isDraggableColumn(column) ? (
              <SortableTableHead
                key={String(column.id)}
                column={column}
                sizeToContent={sizeToContent}
                sortDirection={getSortIcon(column.id)}
                onSortClick={onSortClick}
              />
            ) : (
              <StaticTableHead
                key={String(column.id)}
                column={column}
                pinned={pinned}
                sizeToContent={sizeToContent}
                sortDirection={getSortIcon(column.id)}
                onSortClick={onSortClick}
              />
            ),
          )}
          {hasExpandColumn && <TableHead className="w-10" />}
        </TableRow>
      </SortableContext>
    </DndContext>
  );
}

/** The totals row pinned under the data, when the caller supplies one. */
function SummaryTableRow<T>({
  summaryRow,
  columns,
  pinned,
  stickyFooter,
}: {
  summaryRow: SummaryRow;
  columns: TableColumn<T>[];
  pinned: PinnedColumns;
  stickyFooter: boolean;
}) {
  return (
    <TableRow
      className={cn(
        'border-t-2 font-medium',
        stickyFooter && 'sticky bottom-0 z-10 bg-background',
        // Frozen columns inherit the row's background, so it has to be opaque
        // (see pinnedCellProps).
        !stickyFooter && (pinned.lastId ? 'bg-muted' : 'bg-muted/25'),
      )}
    >
      {columns.map((column) => {
        const pinnedProps = pinnedCellProps(column, pinned);
        return (
          <TableCell
            key={String(column.id)}
            className={cn(
              'font-semibold',
              stickyFooter && 'bg-background',
              column.align === 'center' && 'text-center',
              column.align === 'right' && 'text-right',
              pinnedProps.className,
              column.className,
            )}
            style={pinnedProps.style}
          >
            {summaryRow[String(column.id)] || ''}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

function HeaderCellContent<T>({
  column,
  sortDirection,
}: {
  column: TableColumn<T>;
  sortDirection: 'asc' | 'desc' | null;
}) {
  return (
    <div
      className={cn(
        'flex items-center',
        column.align === 'center' && 'justify-center',
        column.align === 'right' && 'justify-end',
      )}
    >
      {column.headerRender ? (
        column.headerRender()
      ) : (
        <span className="font-medium text-sm">
          {String(column.id) === 'index' ? '#' : column.label}
        </span>
      )}
      <SortIcon direction={sortDirection} sortable={column.sortable} />
    </div>
  );
}

/**
 * Header cell that is draggable for column reordering. Only rendered when
 * `enableColumnReorder` is set; the plain-header path never mounts dnd hooks.
 */
function SortableTableHead<T>({
  column,
  sizeToContent,
  sortDirection,
  onSortClick,
}: {
  column: TableColumn<T>;
  sizeToContent?: boolean;
  sortDirection: 'asc' | 'desc' | null;
  onSortClick: (columnId: keyof T) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(column.id) });

  return (
    <TableHead
      ref={setNodeRef}
      className={cn(
        column.align === 'center' && 'text-center',
        column.align === 'right' && 'text-right',
        column.sortable && 'cursor-pointer select-none',
        sizeToContent && 'whitespace-nowrap',
        isDragging && 'relative z-20 opacity-50',
        column.className,
      )}
      style={{
        minWidth: column.minWidth,
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      onClick={() => {
        if (column.sortable) {
          onSortClick(column.id);
        }
      }}
      {...attributes}
      {...listeners}
      // dnd-kit's attributes carry `role="button"`, which would take a header
      // cell out of the table's own semantics — a `<th>` that no longer names its
      // column to a screen reader. The drag affordance is still announced through
      // the `aria-roledescription` that comes with those attributes.
      role="columnheader"
    >
      <HeaderCellContent column={column} sortDirection={sortDirection} />
    </TableHead>
  );
}

export interface TableColumn<T> {
  id: keyof T;
  label: string;
  sortable?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (value: any, row: T) => React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  headerRender?: () => React.ReactNode;
  /** Extract sortable value from row. Use when column displays derived data not in row[id]. */
  getSortValue?: (row: T) => unknown;
  minWidth?: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
  stopRowClick?: boolean; // If true, clicking this cell won't trigger row click
  /**
   * Freeze this column at the inline start while the rest scroll horizontally.
   * Pinned columns must lead the `columns` array and be contiguous — their
   * offsets are measured by position, see `buildPinnedColumns`.
   */
  pinned?: boolean;
}

export interface SummaryRow {
  [key: string]: React.ReactNode;
}

export interface GentlyTableProps<T> {
  data: T[] | undefined;
  columns: TableColumn<T>[];
  searchPlaceholder?: string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  searchFields?: (keyof T)[];
  showSearch?: boolean;
  /** Custom node rendered in place of the default search input (left of the count). */
  searchSlot?: React.ReactNode;
  /**
   * Rendered between the search/actions header row and the table itself. Use
   * for a filter panel that a control in `actions` toggles, so the toggle sits
   * above the thing it opens.
   */
  belowHeaderSlot?: React.ReactNode;
  actions?: React.ReactNode;
  summaryRow?: SummaryRow;
  isLoading?: boolean;
  className?: string;
  showDownload?: boolean;
  downloadFilename?: string;
  downloadIcon?: React.ReactNode;
  showCount?: boolean; // Show current count of displayed items
  customCountDisplay?: React.ReactNode; // Custom count display component
  getRowClassName?: (row: T) => string; // Custom className for each row
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultCellRender?: (value: any, row: T) => React.ReactNode; // Default render for cells without custom render

  // Scrollable table options
  maxHeight?: number | string; // Max height for table body (e.g., 600 or "600px")
  stickyHeader?: boolean; // Whether to make the header sticky when scrolling
  stickyFooter?: boolean; // Whether to make the summary row sticky at the bottom when scrolling
  /**
   * Size every column to its content instead of stretching the table across
   * its container: cells never wrap, so each column ends up exactly as wide as
   * its widest cell and each row exactly one line tall. The table then scrolls
   * horizontally when the content is wider than the container. Columns keep
   * their `minWidth` if they set one, so a caller wanting true content width
   * leaves it off.
   */
  sizeToContent?: boolean;

  // Advanced overrides for server-side features
  serverSideSearch?: {
    value: string;
    onChange: (value: string) => void;
    /**
     * Renders a VS Code-style `.*` toggle inside the search box for switching
     * between substring and regex matching. Omit to hide the toggle entirely.
     */
    regexToggle?: {
      active: boolean;
      onToggle: () => void;
      /** Flags an invalid pattern (red border) — server rejects it too. */
      invalid?: boolean;
      /** Explanation shown as a tooltip on the search box when invalid. */
      invalidMessage?: string;
    };
  };
  serverSideSorting?: {
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    onSort: (columnId: string, direction: 'asc' | 'desc') => void;
  };
  disableClientSideSort?: boolean;
  downloadAllData?: T[]; // For paginated tables, pass all data here for download
  defaultSort?: {
    sortBy: keyof T;
    sortDirection: SortDirection;
  };
  getRowKey?: (row: T, index: number) => string;
  renderExpandedRow?: (row: T) => React.ReactNode;
  /**
   * Keep each expanded panel mounted after its first open and toggle it with
   * `display:none` instead of unmounting on collapse. Makes collapse/re-expand
   * instant (no rebuild of a heavy panel) at the cost of retained DOM per opened
   * row. Off by default.
   */
  persistExpandedContent?: boolean;
  /**
   * Seeds a row's expanded state when it mounts (e.g. deep-link auto-expand).
   * Evaluated once per row mount — changing the value later does not re-expand
   * an already-mounted row.
   */
  getInitialExpanded?: (row: T) => boolean;
  /** Called after a row's expansion is toggled by clicking it. */
  onExpandedRowChange?: (row: T, expanded: boolean) => void;
  /**
   * Opt-in drag-and-drop reordering of column headers. Controlled: on drop the
   * table emits the new id order of the columns it currently renders and the
   * parent re-renders with a reordered `columns` array — the table applies no
   * ordering itself. Columns whose id starts with '__' (internal columns like
   * the selection checkbox) are not draggable and keep their position.
   */
  enableColumnReorder?: boolean;
  onColumnOrderChange?: (orderedIds: string[]) => void;
  /**
   * Opt-in: render each row as a real link so right-click → "Open in new tab",
   * cmd/ctrl-click, and middle-click all work (native browser behaviour) while
   * plain left-click still navigates in place. Return a TanStack Router `<Link>`
   * (typed `to`/`params` at the call site); the table passes a `className` that
   * stretches it over the whole row. Give the link an `aria-label` for a11y.
   * When set, the link owns navigation — don't also pass `onRowClick` to navigate.
   * The link overlays the cell content; if a cell needs an independently
   * clickable control, give that control `relative z-10` so it rises above it.
   */
  renderRowLink?: (row: T, className: string) => React.ReactNode;
  /**
   * Enables built-in client-side pagination of the filtered + sorted rows and
   * renders a pagination footer. Opt-in; omit for a non-paginated table.
   */
  clientPagination?: {
    pageSize?: number;
    rowsPerPageOptions?: number[];
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface GentlyTableRowProps<T extends Record<string, any>> {
  row: T;
  index: number;
  columns: TableColumn<T>[];
  pinned: PinnedColumns;
  sizeToContent?: boolean;
  renderExpandedRow?: (row: T) => React.ReactNode;
  renderRowLink?: (row: T, className: string) => React.ReactNode;
  onRowClick?: (row: T) => void;
  getRowClassName?: (row: T) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultCellRender?: (value: any, row: T) => React.ReactNode;
  persistExpandedContent?: boolean;
  getInitialExpanded?: (row: T) => boolean;
  onExpandedRowChange?: (row: T, expanded: boolean) => void;
}

/**
 * A single data row (plus its optional expanded panel).
 *
 * Expansion state is colocated here rather than centralized in the parent table
 * on purpose: a row's expand/collapse is its own local state, so toggling one
 * row re-renders only that row — the table itself and every sibling row are
 * left untouched. That keeps expanding/collapsing O(1) regardless of how many
 * rows are already open (a shared expanded-keys set in the parent forced every
 * open row to re-render on each toggle).
 *
 * With `persistExpandedContent`, the panel is mounted on first expand and then
 * kept mounted, toggled with `display:none` instead of being unmounted. Collapse
 * and re-expand become a class flip — no unmount/remount, so the (potentially
 * heavy) panel DOM is built once rather than torn down and rebuilt each time,
 * and a hidden panel drops out of layout. Costs some retained DOM/memory per
 * opened row, so it's opt-in.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function GentlyTableRow<T extends Record<string, any>>({
  row,
  index,
  columns,
  pinned,
  sizeToContent,
  renderExpandedRow,
  renderRowLink,
  onRowClick,
  getRowClassName,
  defaultCellRender,
  persistExpandedContent = false,
  getInitialExpanded,
  onExpandedRowChange,
}: GentlyTableRowProps<T>) {
  const [isExpanded, setIsExpanded] = useState(
    () => getInitialExpanded?.(row) ?? false,
  );
  // Once expanded, keep the panel mounted (hidden when collapsed) if the caller
  // opted into persistence.
  const [hasEverExpanded, setHasEverExpanded] = useState(
    () => getInitialExpanded?.(row) ?? false,
  );

  const handleClick = () => {
    if (renderExpandedRow) {
      const next = !isExpanded;
      setIsExpanded(next);
      setHasEverExpanded(true);
      onExpandedRowChange?.(row, next);
    }
    if (onRowClick) {
      onRowClick(row);
    }
  };

  const showExpandedPanel = persistExpandedContent
    ? hasEverExpanded
    : isExpanded;

  // Stretched link that turns the whole row into a real anchor (right-click /
  // cmd-click / middle-click → new tab). It overlays the cell content (so a
  // click anywhere navigates); a cell needing its own clickable control gives
  // it `relative z-10` to rise above this overlay. Note: the row's `relative`
  // must NOT promote descendant <a>s, or it would override this link's own
  // `absolute` and collapse it.
  const rowLink = renderRowLink?.(row, cn('absolute inset-0 z-[1]'));
  const hasRowLink = rowLink != null && rowLink !== false;

  return (
    <>
      <TableRow
        className={cn(
          (onRowClick || renderExpandedRow || hasRowLink) && 'cursor-pointer',
          hasRowLink && 'relative',
          // A row and the panel it opened read as one block, so the row's own
          // bottom rule is dropped while it is expanded — the divider stays
          // below the panel, where the next row actually starts.
          isExpanded && 'border-b-0',
          pinnedRowClass(pinned, isExpanded),
          getRowClassName?.(row),
        )}
        onClick={handleClick}
      >
        {columns.map((column, colIndex) => {
          const pinnedProps = pinnedCellProps(column, pinned);
          return (
            <TableCell
              key={String(column.id)}
              className={cn(
                column.align === 'center' && 'text-center',
                column.align === 'right' && 'text-right',
                sizeToContent && 'whitespace-nowrap',
                pinnedProps.className,
                column.className,
              )}
              style={pinnedProps.style}
              onClick={
                column.stopRowClick ? (e) => e.stopPropagation() : undefined
              }
            >
              {colIndex === 0 && rowLink}
              <CellContent
                column={column}
                row={row}
                index={index}
                defaultCellRender={defaultCellRender}
              />
            </TableCell>
          );
        })}
        {renderExpandedRow && (
          <TableCell className="w-10 pr-4 text-right">
            <CaretRightIcon
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform',
                isExpanded && 'rotate-90',
              )}
            />
          </TableCell>
        )}
      </TableRow>
      {showExpandedPanel && renderExpandedRow && (
        <TableRow
          className={cn('hover:bg-transparent', !isExpanded && 'hidden')}
        >
          <TableCell
            colSpan={columns.length + 1}
            className="p-0 overflow-hidden"
          >
            <ExpandedRowContent renderFn={renderExpandedRow} row={row} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * A clean, shadcn-based table component for displaying tabular data.
 * Supports client-side search, sorting, and customizable columns.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function GentlyTable<T extends Record<string, any>>({
  data,
  columns,
  searchPlaceholder = 'Search...',
  onRowClick,
  emptyMessage = 'No data found',
  searchFields = EMPTY_SEARCH_FIELDS,
  showSearch = true,
  searchSlot,
  belowHeaderSlot,
  actions,
  summaryRow,
  isLoading = false,
  className,
  showDownload = true,
  downloadFilename = 'table-data',
  downloadIcon,
  showCount = false,
  customCountDisplay,
  getRowClassName,
  defaultCellRender,
  maxHeight,
  stickyHeader = !!maxHeight,
  stickyFooter = false,
  sizeToContent = false,
  serverSideSearch,
  serverSideSorting,
  disableClientSideSort = false,
  downloadAllData,
  defaultSort,
  getRowKey,
  renderExpandedRow,
  persistExpandedContent,
  getInitialExpanded,
  onExpandedRowChange,
  renderRowLink,
  clientPagination,
  enableColumnReorder = false,
  onColumnOrderChange,
}: GentlyTableProps<T>) {
  const gt = useGT();
  const { headerRowRef, pinnedColumns } = usePinnedColumns(columns);
  // Use custom hooks for search and sort logic
  const { searchQuery, setSearchQuery, filteredData } = useTableSearch({
    data,
    searchFields,
    serverSideSearch,
  });

  const {
    sortedData: filteredAndSortedData,
    handleSort,
    getSortIcon,
  } = useTableSort({
    data: filteredData,
    columns,
    serverSideSorting,
    disableClientSideSort,
    defaultSort,
  });

  // A completed header drag can still let the browser fire a click on the drop
  // target, which would read as a sort. Set on drag end, cleared on the next
  // tick, checked in the sort click handler.
  const justDraggedHeaderRef = useRef(false);
  // The 8px activation distance keeps plain clicks (sorting, filter funnels)
  // from ever starting a drag.
  const reorderSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleSortClick = (columnId: keyof T) => {
    if (justDraggedHeaderRef.current) return;
    const column = columns.find((col) => col.id === columnId);
    handleSort(columnId, column?.sortable);
  };

  // The dropped column's new place among *all* the columns — the emitted order is
  // the full id list, and only draggable columns can be either end of a drag (see
  // `isDraggableColumn`), so the columns that stay put keep their index.
  const handleHeaderDragEnd = (event: DragEndEvent) => {
    justDraggedHeaderRef.current = true;
    setTimeout(() => {
      justDraggedHeaderRef.current = false;
    }, 0);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = columns.map((column) => String(column.id));
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onColumnOrderChange?.(arrayMove(ids, from, to));
  };

  // Built-in client-side pagination over the filtered + sorted rows.
  const clientPaginationEnabled = !!clientPagination;
  const [cpPage, setCpPage] = useState(1);
  const [cpPageSize, setCpPageSize] = useState(
    clientPagination?.pageSize ?? 10,
  );
  const cpTotalItems = filteredAndSortedData.length;
  const cpTotalPages = Math.max(1, Math.ceil(cpTotalItems / cpPageSize));
  // Clamp during render so shrinking results (e.g. via search) never strand the
  // view on an out-of-range page without needing an effect to reset it.
  const cpSafePage = Math.min(Math.max(1, cpPage), cpTotalPages);
  const pageRows = clientPaginationEnabled
    ? filteredAndSortedData.slice(
        (cpSafePage - 1) * cpPageSize,
        cpSafePage * cpPageSize,
      )
    : filteredAndSortedData;

  const handleDownload = () => {
    // Use downloadAllData if provided (for paginated tables), otherwise use filtered data
    const dataToDownload = downloadAllData || filteredAndSortedData;

    if (!dataToDownload || dataToDownload.length === 0) {
      return;
    }

    downloadCSV({
      data: dataToDownload,
      filename: downloadFilename,
      columns: columns.map((col) => ({
        id: String(col.id),
        label: col.label,
      })),
    });
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search bar and actions */}
      {(showSearch || searchSlot || actions || showDownload || showCount) && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {searchSlot
              ? searchSlot
              : showSearch && (
                  <TableSearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder={searchPlaceholder}
                    regexToggle={serverSideSearch?.regexToggle}
                  />
                )}
            {showCount &&
              !isLoading &&
              filteredAndSortedData &&
              (customCountDisplay || (
                <div className="text-sm text-muted-foreground">
                  {filteredAndSortedData.length === 1 ? (
                    <T context="showing 1 item count label">
                      Showing <Var>{filteredAndSortedData.length}</Var> item
                    </T>
                  ) : (
                    <T context="showing N items count label">
                      Showing <Var>{filteredAndSortedData.length}</Var> items
                    </T>
                  )}
                </div>
              ))}
          </div>

          <div className="flex gap-2">
            {actions}
            {showDownload && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={
                  !filteredAndSortedData || filteredAndSortedData.length === 0
                }
                title={gt('Download CSV', {
                  context: 'button tooltip to download table as CSV',
                })}
              >
                {downloadIcon ?? <DownloadIcon className="h-4 w-4" />}
              </Button>
            )}
          </div>
        </div>
      )}

      {belowHeaderSlot}

      {/* Table */}
      <div>
        <div
          className="rounded-md border overflow-x-auto"
          style={
            maxHeight
              ? {
                  maxHeight:
                    typeof maxHeight === 'number'
                      ? `${maxHeight}px`
                      : maxHeight,
                  overflowY: 'auto',
                }
              : undefined
          }
        >
          <Table
            // `w-auto` lets the table shrink-wrap its columns; the default
            // `w-full` would stretch them past their content to fill the row.
            className={sizeToContent ? 'w-auto' : undefined}
            style={renderExpandedRow ? { tableLayout: 'fixed' } : undefined}
          >
            <TableHeader
              className={
                stickyHeader
                  ? cn(
                      // `[&_tr]:border-b-0` drops TableHeader's own faint row
                      // border, which lands exactly where the edge line below
                      // goes; same variant, so tailwind-merge replaces it and
                      // one line is drawn instead of two stacked.
                      'sticky top-0 z-10 bg-background [&_tr]:border-b-0',
                      FROZEN_HEADER_EDGE_CLASS,
                    )
                  : undefined
              }
            >
              {enableColumnReorder ? (
                <ReorderableHeaderRow<T>
                  ref={headerRowRef}
                  columns={columns}
                  pinned={pinnedColumns}
                  sizeToContent={sizeToContent}
                  hasExpandColumn={!!renderExpandedRow}
                  getSortIcon={getSortIcon}
                  onSortClick={handleSortClick}
                  sensors={reorderSensors}
                  onDragEnd={handleHeaderDragEnd}
                />
              ) : (
                <PlainHeaderRow<T>
                  ref={headerRowRef}
                  columns={columns}
                  pinned={pinnedColumns}
                  sizeToContent={sizeToContent}
                  hasExpandColumn={!!renderExpandedRow}
                  getSortIcon={getSortIcon}
                  onSortClick={handleSortClick}
                />
              )}
            </TableHeader>
            <TableBody>
              {/* No data yet (first mount, query still pending) reads as
                  loading — never as an empty result. */}
              {isLoading || data === undefined ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (renderExpandedRow ? 1 : 0)}
                    className="h-24 text-center"
                  >
                    <div className="flex items-center justify-center">
                      <div className="text-sm text-muted-foreground">
                        Loading...
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredAndSortedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (renderExpandedRow ? 1 : 0)}
                    className="h-24 text-center"
                  >
                    <div className="flex items-center justify-center">
                      <div className="text-sm text-muted-foreground">
                        {searchQuery
                          ? `No results found matching "${searchQuery}"`
                          : emptyMessage}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {pageRows.map((row, index) => {
                    const rowKey = getRowKey
                      ? getRowKey(row, index)
                      : String(index);
                    return (
                      <GentlyTableRow<T>
                        key={rowKey}
                        row={row}
                        index={index}
                        columns={columns}
                        pinned={pinnedColumns}
                        sizeToContent={sizeToContent}
                        renderExpandedRow={renderExpandedRow}
                        persistExpandedContent={persistExpandedContent}
                        getInitialExpanded={getInitialExpanded}
                        onExpandedRowChange={onExpandedRowChange}
                        renderRowLink={renderRowLink}
                        onRowClick={onRowClick}
                        getRowClassName={getRowClassName}
                        defaultCellRender={defaultCellRender}
                      />
                    );
                  })}

                  {summaryRow && filteredAndSortedData.length > 0 && (
                    <SummaryTableRow<T>
                      summaryRow={summaryRow}
                      columns={columns}
                      pinned={pinnedColumns}
                      stickyFooter={stickyFooter}
                    />
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </div>
        {clientPaginationEnabled && cpTotalItems > 0 && (
          <TablePagination
            pagination={{
              pageSize: cpPageSize,
              currentPage: cpSafePage,
              hasNextPage: cpSafePage < cpTotalPages,
              hasPreviousPage: cpSafePage > 1,
              totalItems: cpTotalItems,
              startItem:
                cpTotalItems === 0 ? 0 : (cpSafePage - 1) * cpPageSize + 1,
              endItem: Math.min(cpSafePage * cpPageSize, cpTotalItems),
            }}
            onPageSizeChange={(size) => {
              setCpPageSize(size);
              setCpPage(1);
            }}
            onPreviousPage={() => setCpPage(Math.max(1, cpSafePage - 1))}
            onNextPage={() => setCpPage(cpSafePage + 1)}
            onFirstPage={() => setCpPage(1)}
            onLastPage={() => setCpPage(cpTotalPages)}
            onGoToPage={(p) => setCpPage(p)}
            showRowsPerPage
            showPageInfo
            showPageNumbers
            rowsPerPageOptions={clientPagination?.rowsPerPageOptions}
          />
        )}
      </div>
    </div>
  );
}

export default GentlyTable;
